/**
 * Authentication hooks using TanStack Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { authService } from '@/api'
import { useAuthStore } from '@/stores/authStore'
import { useUIStore } from '@/stores/uiStore'
import { handleApiError } from '@/lib/apiErrorHandler'
import type { UserMe } from '@/types/api'

/**
 * Query key for current user
 */
export const authKeys = {
  all: ['auth'] as const,
  me: () => [...authKeys.all, 'me'] as const,
}

/**
 * Hook to get current authenticated user
 */
export function useCurrentUser() {
  const { setUser, logout } = useAuthStore()

  return useQuery<UserMe>({
    queryKey: authKeys.me(),
    queryFn: async () => {
      try {
        const user = await authService.getCurrentUser()
        setUser(user)
        return user
      } catch (error) {
        const apiError = error as { status?: number }
        if (apiError?.status === 401) {
          logout()
        }
        throw error
      }
    },
    retry: (failureCount, error) => {
      if ((error as { status?: number })?.status === 401) return false
      return failureCount < 2
    },
    retryDelay: (attemptIndex) => Math.min(1000 * (attemptIndex + 1), 3000),
    staleTime: 1000 * 60 * 10, // 10 minutes
  })
}

/**
 * Hook for login mutation
 */
export function useLogin() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { login } = useAuthStore()
  const { addToast } = useUIStore()

  return useMutation({
    mutationFn: (credentials: { username: string; password: string }) =>
      authService.login(credentials),
    onSuccess: (data) => {
      login(data.user, data.access_token)
      queryClient.setQueryData(authKeys.me(), data.user)
      addToast({
        title: 'Welcome back!',
        description: `Logged in as ${data.user.username}`,
        variant: 'success',
      })
      navigate('/dashboard')
    },
    onError: async (error: { detail?: string; status?: number }) => {
      if (error.status === 429) {
        addToast({
          title: 'Too many attempts',
          description: 'Please wait a minute before trying again.',
          variant: 'error',
        })
      } else if (error.status === 401) {
        addToast({
          title: 'Login failed',
          description: 'Invalid username or password.\nPlease try again.',
          variant: 'error',
        })
      } else {
        handleApiError(error, 'Login failed')
      }
    },
  })
}

/**
 * Hook for logout
 */
export function useLogout() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { logout } = useAuthStore()
  const { addToast } = useUIStore()

  return async () => {
    try {
      await authService.logout()
    } catch {
      // Proceed with local logout even if the API call fails
    }
    logout()
    queryClient.clear()
    addToast({
      title: 'Logged out',
      description: 'You have been logged out successfully',
      variant: 'default',
    })
    navigate('/login')
  }
}
